/**
 * The boot mark: Kivi thinking (the guide's "Loading" film: the dots travel
 * out of the line, the soft glow, the breath), as an SVG that animates itself
 * with CSS keyframes sampled at build time from the one model
 * (`@alluzo/shared/kivi`; docs/plans/kivi-orb.md, step 6.4).
 *
 * **Why CSS, not a script.** The boot screen is the first thing the page
 * draws, before any script (index.html), and it stays only while the scripts
 * and the database load. A CSS animation runs as soon as the page has its
 * styles, so Kivi moves from the first frame, and transforms and opacities run
 * on the compositor, off the main thread the loading scripts are busy on.
 *
 * **One string, two places.** The Vite config writes `bootMarkSvg()` into
 * index.html, and `boot-mark.tsx` draws the same string, so React's first
 * draw is the drawing the page already shows (e2e/shell.spec.ts holds the two
 * equal).
 *
 * **What moves, and how.** Thinking's motions run on periods of their own, so
 * each is its own animation, sampled from the model over its own period and
 * started at the page's start, which is the model's `t = 0`:
 *
 * - the breath: the body's lean is fixed and its squash breathes; one
 *   transform on the body, and the same on the dots behind it;
 * - the dots: three dots travel along the line, each a translate and an
 *   opacity, the later two the first one's loop a third and two thirds on;
 * - the glow: one opacity on a radial gradient;
 * - the floor shadow: its width follows the breath, one transform;
 * - the blink: the one place a shape changes (open almond eyes close to
 *   lids), so it is a short flipbook of whole eye frames, each shown by an
 *   opacity that steps.
 *
 * The animated elements that are the SVG's own children carry their whole
 * move from the SVG's corner in their transform, and the SVG's units start at
 * that corner, so a transform's origin, `0 0`, is that corner in every
 * browser; the dots inside the body only translate, which no origin changes.
 *
 * **Reduced motion** turns the animations off, and what is left is each
 * element's own geometry and opacity: thinking's still frame.
 *
 * Colours are the `agent.kivi.*` tokens, as plain values (`kiviCss`); the rim
 * shows on the dark scheme only, as Kivi's rim does from 24 px up.
 *
 * Pure, and imported by the Vite config: relative imports only.
 */
import {
  DISC,
  discDots,
  kiviCutOf,
  kiviFrame,
  kiviStillOf,
  kiviThinking,
  PIV,
  type KiviCut,
  type KiviFrame,
  type KiviMatrix,
  type KiviOp,
  type KiviPose,
  type KiviRole,
} from '../../../../../packages/shared/src/kivi/index';
import { kiviCss } from '../../graphics/tokens';

/** The drawn disc's width on the boot screen, px. */
export const BOOT_MARK_SIZE = 56;

/** Where the Vite config writes the mark into index.html. */
export const BOOT_MARK_SLOT = '<!--boot-mark-->';

/** The disc's left and top edge in the model's 100 box, and its width: the SVG's box. */
export const DISC_EDGE = 5.94;
export const DISC_UNITS = 88.12;

/**
 * Thinking's periods, s, as `kiviThinking` runs them: the breath at 0.3 Hz,
 * the glow at 0.14 Hz, the dots at 0.9 of the line a second, the blink every
 * 5 s. `boot-mark-svg.test.ts` holds each against the model.
 */
export const PERIODS = { breath: 1 / 0.3, glow: 1 / 0.14, dots: 1 / 0.9, blink: 5 } as const;

/** Samples across a period: enough that straight lines between them read as the curve. */
const BREATH_STEPS = 16;
const GLOW_STEPS = 12;
const DOT_STEPS = 24;
/** The blink closes and opens in the first 0.16 s of its period, drawn as this many frames. */
export const BLINK_SPAN = 0.16;
const BLINK_FRAMES = 5;

/** The state the boot screen shows. */
const pose = kiviThinking;
/** Thinking's still: the frame reduced motion holds. */
export const STILL_T = kiviStillOf('thinking');

/** The boot mark's cut, light and glow: a Kivi of its size. */
export const bootCut = (): ReturnType<typeof kiviCutOf> => kiviCutOf(BOOT_MARK_SIZE);

const fixed = (value: number, places: number): string => String(Number(value.toFixed(places)) || 0);
/** A length in the model's units: a hundredth is a hundredth of a pixel at the boot screen's size. */
const n = (value: number): string => fixed(value, 2);
const pct = (share: number): string => `${fixed(share * 100, 3)}%`;
const ms = (seconds: number): string => `${fixed(seconds * 1000, 0)}ms`;

const isIdentity = (m: KiviMatrix): boolean =>
  Math.abs(m[0] - 1) < 1e-9 && Math.abs(m[1]) < 1e-9 && Math.abs(m[2]) < 1e-9 && Math.abs(m[3] - 1) < 1e-9 && Math.abs(m[4]) < 1e-9 && Math.abs(m[5]) < 1e-9;

/** The pose with the body's own move taken out: the shapes as they stand inside the body's transform. */
const bodyLocal = (P: KiviPose): KiviPose => ({ ...P, rot: 0, tx: 0, ty: 0, sx: 1, sy: 1, glow: 0 });

/** The body's transform from the SVG's corner: its lean on its pivot, then its squash on its foot. CSS or attribute. */
const bodyMove = (P: KiviPose, css: boolean): string => {
  const [px, py] = PIV[P.piv];
  const u = css ? 'px' : '';
  const s = css ? ',' : ' ';
  return (
    `translate(${n(P.tx + px - DISC_EDGE)}${u}${s}${n(P.ty + py - DISC_EDGE)}${u}) rotate(${n(P.rot)}${css ? 'deg' : ''}) ` +
    `translate(${n(50 - px)}${u}${s}${n(94 - py)}${u}) scale(${fixed(P.sx, 5)}${s}${fixed(P.sy, 5)}) translate(-50${u}${s}-94${u})`
  );
};

/** The floor shadow's move: to its place, its width following the breath. CSS or attribute. */
const shadowMove = (P: KiviPose, css: boolean): string => {
  const u = css ? 'px' : '';
  const s = css ? ',' : ' ';
  return `translate(${n(50 + P.tx - DISC_EDGE)}${u}${s}${n(96.2 - DISC_EDGE)}${u}) scale(${fixed(P.sx, 5)}${s}1)`;
};

/** Each role's colour, from the `agent.kivi` tokens; thinking is never tinted. */
const COLOURS = ((): Readonly<Record<KiviRole, string>> => {
  const c = kiviCss(false);
  return { body: c.lapis, under: c.deep, feature: c.paper, light: c.light, shadow: c.shadow, glow: c.light, pebble1: c.pebble1, pebble2: c.pebble2, pebble3: c.pebble3 };
})();

/** One operation as markup, inside the body's transform. A clipped one is clipped to `clip`, when given. */
const opMarkup = (op: KiviOp, clip: string | null): string => {
  const colour = COLOURS[op.role];
  const m = op.transform;
  const transform = m === null || isIdentity(m) ? '' : ` transform="matrix(${[...m.slice(0, 4).map((v) => fixed(v, 4)), n(m[4]), n(m[5])].join(' ')})"`;
  const fade = op.opacity === 1 ? '' : ` opacity="${n(op.opacity)}"`;
  const paint =
    op.paint === 'stroke'
      ? ` fill="none" stroke="${colour}" stroke-width="${n(op.strokeWidth)}"${op.cap === 'round' ? ' stroke-linecap="round"' : ''}${fade}`
      : ` fill="${colour}"${fade}`;
  const shape =
    op.kind === 'path'
      ? `<path d="${op.d}"${transform}${paint}/>`
      : op.kind === 'circle'
        ? `<circle cx="${n(op.cx)}" cy="${n(op.cy)}" r="${n(op.r)}"${transform}${paint}/>`
        : `<ellipse cx="${n(op.cx)}" cy="${n(op.cy)}" rx="${n(op.rx)}" ry="${n(op.ry)}"${transform}${paint}/>`;
  return op.clip && clip !== null ? `<g clip-path="url(#${clip})">${shape}</g>` : shape;
};

/** A frame of the body alone, in its own space: no glow, no shadow, no dots; the eyes or the line left out when asked. */
const localFrame = (P: KiviPose, look: { cut: KiviCut; lit: boolean; rim?: boolean }, parts: { eyes: boolean; line: boolean }): KiviFrame =>
  kiviFrame(
    {
      ...bodyLocal(P),
      eyes: parts.eyes ? P.eyes : { ...P.eyes, hide: true },
      line: { ...P.line, dots: 0, ...(parts.line ? {} : { hide: true }) },
    },
    { cut: look.cut, lit: look.lit, rim: look.rim === true, shadow: false },
  );

/** Times across a period: `steps` of them, and its end. */
const across = (period: number, steps: number): number[] => Array.from({ length: steps + 1 }, (_, k) => (period * k) / steps);

/** The breath's samples: the body's and the shadow's transform at each share of the period. */
export const breathKeys = (): { readonly at: number; readonly body: string; readonly shadow: string }[] =>
  across(PERIODS.breath, BREATH_STEPS).map((t) => {
    const P = pose(t);
    return { at: t / PERIODS.breath, body: bodyMove(P, true), shadow: shadowMove(P, true) };
  });

/** The glow's samples: its strength at each share of the period. */
export const glowKeys = (): { readonly at: number; readonly opacity: number }[] =>
  across(PERIODS.glow, GLOW_STEPS).map((t) => ({ at: t / PERIODS.glow, opacity: pose(t).glow }));

/**
 * The first dot's samples over its loop: where it stands on the line and how
 * strongly it shows. Its loop ends where it fades out at the line's tip, just
 * before it starts again by the face, so the jump back is never seen.
 */
export const dotKeys = (cut: KiviCut): { readonly at: number; readonly x: number; readonly y: number; readonly opacity: number }[] =>
  across(PERIODS.dots, DOT_STEPS).map((t, k) => {
    const first = discDots(pose(k === DOT_STEPS ? t - 1e-6 : t).line, cut)[0];
    if (first === undefined || first.shape.kind !== 'circle') throw new Error('Thinking has no dots.');
    return { at: t / PERIODS.dots, x: first.shape.cx, y: first.shape.cy, opacity: first.opacity ?? 1 };
  });

/** One stretch of the blink's period and the eyes it shows. */
export type BlinkSpan = { readonly from: number; readonly to: number; readonly eyes: string };

/**
 * The blink as a flipbook over its period, as shares of it: `BLINK_FRAMES`
 * stretches across the blink, each drawn at its middle, then the open eyes
 * for the rest. Stretches that draw the same eyes are one.
 */
export const blinkSpans = (cut: KiviCut, lit: boolean): BlinkSpan[] => {
  const eyesAt = (t: number): string =>
    localFrame(pose(t), { cut, lit }, { eyes: true, line: false })
      .ops.filter((op) => op.role === 'feature')
      .map((op) => opMarkup(op, null))
      .join('');
  const spans: BlinkSpan[] = [];
  const push = (from: number, to: number, eyes: string): void => {
    const last = spans.at(-1);
    if (last !== undefined && last.eyes === eyes) spans[spans.length - 1] = { ...last, to };
    else spans.push({ from, to, eyes });
  };
  const step = BLINK_SPAN / BLINK_FRAMES;
  for (let k = 0; k < BLINK_FRAMES; k += 1) push((k * step) / PERIODS.blink, ((k + 1) * step) / PERIODS.blink, eyesAt((k + 0.5) * step));
  push(BLINK_SPAN / PERIODS.blink, 1, eyesAt(STILL_T));
  return spans;
};

/** The distinct eye frames, the open one (the still's) first, and each one's opacity at every change. */
const blinkFrames = (spans: readonly BlinkSpan[]): { readonly eyes: string; readonly keys: string }[] => {
  const open = spans.at(-1)?.eyes ?? '';
  const frames = [open, ...new Set(spans.map(({ eyes }) => eyes).filter((eyes) => eyes !== open))];
  return frames.map((eyes) => ({
    eyes,
    keys: [...spans.map(({ from, eyes: shown }) => `${pct(from)}{opacity:${shown === eyes ? '1' : '0'}}`), `100%{opacity:${spans.at(-1)?.eyes === eyes ? '1' : '0'}}`].join(''),
  }));
};

const NAME = 'boot-kivi';

const style = (cut: KiviCut, frames: readonly { readonly keys: string }[]): string => {
  const breath = breathKeys();
  const loop = (name: string, period: number, timing = 'linear'): string => `.${NAME}-${name}{animation:${NAME}-${name} ${ms(period)} ${timing} infinite}`;
  const keyframes = (name: string, stops: string): string => `@keyframes ${NAME}-${name}{${stops}}`;
  return [
    `.${NAME}-a{transform-box:view-box;transform-origin:0 0}`,
    loop('body', PERIODS.breath),
    loop('shadow', PERIODS.breath),
    loop('glow', PERIODS.glow),
    loop('dot', PERIODS.dots),
    `.${NAME}-dot1{animation-delay:-${ms(PERIODS.dots / 3)}}.${NAME}-dot2{animation-delay:-${ms((PERIODS.dots * 2) / 3)}}`,
    ...frames.map((_, index) => loop(`eyes${String(index)}`, PERIODS.blink, 'step-end')),
    `.${NAME}-rim{display:none}.dark .${NAME}-rim{display:inline}`,
    `@media (prefers-reduced-motion:reduce){.${NAME}-a{animation:none}}`,
    keyframes('body', breath.map(({ at, body }) => `${pct(at)}{transform:${body}}`).join('')),
    keyframes('shadow', breath.map(({ at, shadow }) => `${pct(at)}{transform:${shadow}}`).join('')),
    keyframes('glow', glowKeys().map(({ at, opacity }) => `${pct(at)}{opacity:${fixed(opacity, 3)}}`).join('')),
    keyframes('dot', dotKeys(cut).map(({ at, x, y, opacity }) => `${pct(at)}{transform:translate(${n(x)}px,${n(y)}px);opacity:${fixed(opacity, 3)}}`).join('')),
    ...frames.map(({ keys }, index) => keyframes(`eyes${String(index)}`, keys)),
  ].join('');
};

/**
 * The boot mark, a disc `size` across, its clip and glow named after `id`.
 * Still, or with reduced motion, it is thinking's still frame.
 */
export const bootMarkSvg = ({ size = BOOT_MARK_SIZE, id }: { size?: number; id: string }): string => {
  const { cut, lit } = bootCut();
  const still = pose(STILL_T);
  const clip = `${id}-disc`;
  const glowId = `${id}-glow`;
  const frames = blinkFrames(blinkSpans(cut, lit));

  // The glow: a radial gradient at its full strength, its opacity the glow's.
  const glowOp = kiviFrame({ ...still, glow: 1 }, { cut, lit, shadow: false }).ops.find((op) => op.role === 'glow');
  let glow = '';
  let glowShape = '';
  if (glowOp?.kind === 'circle') {
    const [cx, cy, r, o] = [n(glowOp.cx - DISC_EDGE), n(glowOp.cy - DISC_EDGE), n(glowOp.r), n(glowOp.opacity)];
    glow =
      `<radialGradient id="${glowId}" gradientUnits="userSpaceOnUse" cx="${cx}" cy="${cy}" r="${r}">` +
      `<stop offset="0" stop-color="${COLOURS.glow}" stop-opacity="${o}"/><stop offset="0.55" stop-color="${COLOURS.glow}" stop-opacity="${o}"/>` +
      `<stop offset="1" stop-color="${COLOURS.glow}" stop-opacity="0"/></radialGradient>`;
    glowShape = `<circle class="${NAME}-a ${NAME}-glow" cx="${cx}" cy="${cy}" r="${r}" fill="url(#${glowId})" opacity="${fixed(still.glow, 3)}"/>`;
  }

  // The floor shadow, drawn about its own centre at no squash and moved to its place.
  const shadowOp = kiviFrame(bodyLocal(still), { cut, lit }).ops.find((op) => op.role === 'shadow');
  const shadow =
    shadowOp?.kind === 'ellipse'
      ? `<ellipse class="${NAME}-a ${NAME}-shadow" rx="${n(shadowOp.rx)}" ry="${n(shadowOp.ry)}" fill="${COLOURS.shadow}" opacity="${n(shadowOp.opacity)}" transform="${shadowMove(still, false)}"/>`
      : '';

  // The three dots at the still's places: behind the body in its colour, and in front of it, clipped, in paper.
  const stillDots = discDots(still.line, cut);
  const dots = (role: KiviRole): string =>
    stillDots
      .map(({ shape, opacity }, index) =>
        shape.kind === 'circle'
          ? `<circle class="${NAME}-a ${NAME}-dot ${NAME}-dot${String(index)}" r="${n(shape.r)}" fill="${COLOURS[role]}" transform="translate(${n(shape.cx)} ${n(shape.cy)})" opacity="${fixed(opacity ?? 1, 3)}"/>`
          : '',
      )
      .join('');

  // The body: the disc lit, the rim on the dark scheme, the eyes as a flipbook, the line, then the dots in front.
  const ops = localFrame(still, { cut, lit }, { eyes: false, line: true }).ops;
  const disc = ops.filter((op) => op.role !== 'feature').map((op) => opMarkup(op, clip)).join('');
  const line = ops.filter((op) => op.role === 'feature').map((op) => opMarkup(op, null)).join('');
  const rimOp = localFrame(still, { cut, lit, rim: true }, { eyes: false, line: false }).ops.find((op) => op.paint === 'stroke');
  const rim = rimOp === undefined ? '' : opMarkup(rimOp, null).replace('<path ', `<path class="${NAME}-rim" `);
  const eyes = frames
    .map(({ eyes: drawn }, index) => `<g class="${NAME}-a ${NAME}-eyes${String(index)}"${index === 0 ? '' : ' opacity="0"'}>${drawn}</g>`)
    .join('');
  const body = `${disc}${rim}<g clip-path="url(#${clip})">${eyes}${line}${dots('feature')}</g>`;
  const move = bodyMove(still, false);

  return (
    `<svg aria-hidden="true" width="${String(size)}" height="${String(size)}" viewBox="0 0 ${n(DISC_UNITS)} ${n(DISC_UNITS)}" class="shrink-0" style="overflow:visible">` +
    `<style>${style(cut, frames)}</style><defs>${glow}<clipPath id="${clip}"><path d="${DISC}"/></clipPath></defs>` +
    `${glowShape}${shadow}<g class="${NAME}-a ${NAME}-body" transform="${move}">${dots('body')}</g>` +
    `<g class="${NAME}-a ${NAME}-body" transform="${move}">${body}</g></svg>`
  );
};
